import React, { useState, useEffect } from 'react';
import { Button, Space, message, Typography, Tabs, Table, Select, Descriptions, Tag, Modal, Tooltip } from 'antd';
import SvgLineChart from './SvgLineChart';
import TradeTimelineModal from './TradeTimelineModal';
import AlertDetailModal from './AlertDetailModal';
import ClauseDetailModal from './ClauseDetailModal';
import RebalancingSelectionModal from './RebalancingSelectionModal';
// DAS-1778: Import SearchResultModal for ticker details
import ThreeTablesModal from '../SearchResultModal';
import getUserId from '../../functions/getUserId';

const ResultsDrawer = ({
  runInfo,
  resultsData,
  warningsData,
  exportResultsCsv,
  copyResultsJson,
  downloadResultsJson,
  downloadPricesAuditJson,
  copyRunConfigJson,
  downloadRunConfigJson,
  createRunJiraTicket,
  universeJson,
  setUniverseOpen,
  eqCheck,
  twrSummary,
  performanceMetrics, // Receive new prop
  callAction,
  setEqCheck,
  setEqCheckOpen,
  portfolioSummary,
  cashData,
  cashTransactions,
  overlayData,
  equityData,
  benchmarkData,
  secondaryBenchmarkData,  // DAS-1659
  excessData,
  excessLineColor,
  excessTransactions,
  fxSummary,
  loadPricesDebugData,
  pricesDebugLoading,
  pricesDebugData,
  selectedDebugDate,
  setSelectedDebugDate,
  selectedDateDebugData,
  // Events list for table
  eventsList,
  // Trade Timeline props
  tradeTimelineModal,
  openTradeTimeline,
  closeTradeTimeline,
  copyTradeTimelineJson,
  createTradeTimelineJiraTicket,
  // Alert & Clause Details props
  alertDetailModal,
  openAlertDetails,
  closeAlertDetails,
  clauseDetailModal,
  openClauseDetails,
  closeClauseDetails,
  // DAS-1662: Rebalancing Selection Details props
  rebalancingModal,
  openRebalancingDetails,
  closeRebalancingDetails,
  // DAS-1683: Open configuration for editing
  onOpenConfiguration,
}) => {
  // DAS-1714: Ticker events detail modal state
  const [tickerDetailModal, setTickerDetailModal] = useState({ open: false, ticker: null, data: null, sector: null });
  
  // DAS-1778: SearchResultModal state for viewing full ticker details
  const [searchResultModal, setSearchResultModal] = useState({ open: false, ticker: null });
  
  // DAS-1716: Sector overview now comes from backend (resultsData.sector_overview)
  // No more frontend computation needed - much faster!
  const sectorOverview = resultsData?.sector_overview || null;
  
  // DAS-1716: Portfolio summary now comes from backend (resultsData.portfolio_summary)
  // Falls back to prop for backwards compatibility
  const effectivePortfolioSummary = resultsData?.portfolio_summary || portfolioSummary;
  
  // DAS-1751: Ticker metadata cache for company info tooltips and delisting status
  const [tickerMetadata, setTickerMetadata] = useState({});
  
  // DAS-1762: Filtered stocks state for momentum filter visibility
  const [filteredStocks, setFilteredStocks] = useState(null);
  const [filteredStocksExpanded, setFilteredStocksExpanded] = useState(false);
  
  // DAS-1857: Regime performance analysis state
  const [regimePerformance, setRegimePerformance] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(false);
  
  // DAS-1751: Fetch ticker metadata when portfolio summary changes
  useEffect(() => {
    const fetchTickerMetadata = async () => {
      const tickers = effectivePortfolioSummary?.tickers?.map(t => t.ticker) || [];
      const eventTickers = (eventsList || []).map(e => e.ticker).filter(Boolean);
      const allTickers = [...new Set([...tickers, ...eventTickers])];
      
      if (allTickers.length === 0 || !callAction) return;
      
      try {
        const res = await callAction('get_ticker_sectors_batch', { tickers: allTickers }, { useLoader: false });
        if (res?.success && res.data) {
          // DAS-1751: Response is now { ticker: {...metadata...} } directly
          setTickerMetadata(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch ticker metadata:', err);
      }
    };
    
    fetchTickerMetadata();
  }, [effectivePortfolioSummary, eventsList, callAction]);
  
  // DAS-1857: Fetch regime performance when run changes
  useEffect(() => {
    const fetchRegimePerformance = async () => {
      if (!runInfo?.run_id || !callAction) {
        setRegimePerformance(null);
        return;
      }
      
      setRegimeLoading(true);
      try {
        const res = await callAction('get_regime_performance', { run_id: runInfo.run_id }, { useLoader: false });
        if (res?.success && res.data?.regime_performance) {
          setRegimePerformance(res.data);
        } else {
          setRegimePerformance(null);
        }
      } catch (err) {
        console.error('Failed to fetch regime performance:', err);
        setRegimePerformance(null);
      } finally {
        setRegimeLoading(false);
      }
    };
    
    fetchRegimePerformance();
  }, [runInfo?.run_id, callAction]);
  
  // DAS-1762: Fetch filtered stocks when run changes
  useEffect(() => {
    const fetchFilteredStocks = async () => {
      if (!runInfo?.run_id || !callAction) {
        setFilteredStocks(null);
        return;
      }
      
      try {
        const res = await callAction('get_filtered_stocks_for_rebalancing', { run_id: runInfo.run_id }, { useLoader: false });
        if (res?.success && res.data) {
          setFilteredStocks(res.data);
        } else {
          setFilteredStocks(null);
        }
      } catch (err) {
        console.error('DAS-1762: Failed to fetch filtered stocks:', err);
        setFilteredStocks(null);
      }
    };
    
    fetchFilteredStocks();
  }, [runInfo?.run_id, callAction]);
  
  // DAS-1751: Render ticker with delisting indicator and tooltip
  // DAS-1762: Added momentum to tooltip for selected/filtered stocks
  const renderTickerWithInfo = (ticker) => {
    const meta = tickerMetadata[ticker] || {};
    const isDelisted = meta.delisting_date && new Date(meta.delisting_date) <= new Date();
    
    // DAS-1762: Get momentum info if available
    // Check if this ticker is in filtered_stocks (whether passed or filtered)
    const allStocksInRun = filteredStocks?.filtered_stocks || [];
    const tickerMomentum = allStocksInRun.find(s => s.ticker?.toUpperCase() === ticker?.toUpperCase());
    
    const tooltipContent = (
      <div style={{ maxWidth: 280 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{meta.company_name || ticker}</div>
        {meta.sector && <div><strong>Sector:</strong> {meta.sector}</div>}
        {meta.industry && <div><strong>Industry:</strong> {meta.industry}</div>}
        {/* DAS-1762: Show momentum if available */}
        {tickerMomentum?.momentum_value != null && (
          <div>
            <strong>Momentum (12-1):</strong>{' '}
            <span style={{ 
              color: tickerMomentum.momentum_value >= 0 ? '#52c41a' : '#cf1322',
              fontWeight: 500
            }}>
              {tickerMomentum.momentum_value >= 0 ? '+' : ''}{(tickerMomentum.momentum_value * 100).toFixed(1)}%
            </span>
          </div>
        )}
        {meta.list_date && <div><strong>Listed:</strong> {meta.list_date}</div>}
        {meta.delisting_date && <div style={{ color: '#cf1322' }}><strong>Delisted:</strong> {meta.delisting_date}</div>}
      </div>
    );
    
    return (
      <Tooltip title={tooltipContent} placement="topLeft">
        <span style={{ cursor: 'help' }}>
          {ticker}
          {isDelisted && <span style={{ color: '#cf1322', fontSize: 11, marginLeft: 2 }}>(d)</span>}
        </span>
      </Tooltip>
    );
  };
  const renderEventReason = (record) => {
    const meta = record.reason_meta;
    if (!meta || !meta.mode) {
      return record.reason || '-';
    }
    if (meta.mode === 'alert' && meta.alert_id) {
      return (
        <span>
          {meta.action || 'Trade'} {record.ticker}
          ,{' '}
          <Button type="link" size="small" onClick={() => openAlertDetails(meta.alert_id)}>
            Alert {meta.alert_id}
          </Button>
        </span>
      );
    }
    if (meta.mode === 'clauses') {
      const clauseIds = meta.clause_ids || [];
      const clauseLabel = clauseIds.length
        ? `${clauseIds.length > 1 ? 'Clauses' : 'Clause'} ${clauseIds.join(', ')}`
        : 'Clauses';
      return (
        <span>
          {meta.action || 'Trade'} {record.ticker}
          ,{' '}
          <Button type="link" size="small" onClick={() => openClauseDetails(meta, record)}>
            {clauseLabel}
          </Button>
        </span>
      );
    }
    if (meta.mode === 'clause_set' && meta.clause_set_id) {
      return (
        <span>
          {meta.action || 'Trade'} {record.ticker}
          , clause set {meta.clause_set_id}
        </span>
      );
    }
    return meta.label || record.reason || '-';
  };

  return (
    <>
      <Tabs
        defaultActiveKey="results"
        items={[
          {
            key: 'results',
            label: 'Results',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* DAS-1611: Performance Summary Card */}
                {(twrSummary || performanceMetrics) && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px',
                    flexWrap: 'wrap',
                    marginBottom: '16px' 
                  }}>
                    <div style={{ 
                      flex: '1 1 300px',
                      padding: '16px', 
                      background: '#fff',
                      border: '1px solid #e8e8e8', 
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Typography.Title level={5} style={{ marginTop: 0 }}>Performance Summary</Typography.Title>
                      
                      <div style={{ marginBottom: 12, fontSize: 13 }}>
                         <div style={{ fontWeight: 500, color: '#1890ff' }}>
                           {(() => {
                             const cfg = runInfo?.config;
                             if (cfg?.scenario) return cfg.scenario;
                             if (cfg?.backtest_clause_set_id) return `Clause Set #${cfg.backtest_clause_set_id}`;
                             if (cfg?.backtest_clause_ids && cfg.backtest_clause_ids.length > 0) {
                               return `Clauses (${cfg.backtest_clause_ids.length})`;
                             }
                             if (cfg?.alert_ids && cfg.alert_ids.length > 0) {
                               return `Alerts (${cfg.alert_ids.length})`;
                             }
                             if (cfg?.rebalancing?.enabled) return 'Rebalancing Strategy';
                             return runInfo?.name || 'Custom Configuration';
                           })()}
                         </div>
                         <div style={{ color: '#666' }}>
                           {runInfo?.config?.start_date} to {runInfo?.config?.end_date}
                         </div>
                      </div>

                      <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Strategy Total Return">
                          <span style={{ color: (twrSummary?.strategy_twr ?? performanceMetrics?.strategyReturn) >= 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>
                            {twrSummary?.strategy_twr != null 
                              ? `${(twrSummary.strategy_twr * 100).toFixed(2)}% (TWR)` 
                              : (performanceMetrics?.strategyReturn != null ? `${(performanceMetrics.strategyReturn * 100).toFixed(2)}%` : '-')}
                            {/* DAS-1776: Show EUR gain/loss */}
                            {resultsData?.start_equity_eur && resultsData?.end_equity_eur && (
                              <span style={{ marginLeft: 8, fontWeight: 'normal', color: '#666' }}>
                                (€{(resultsData.start_equity_eur).toLocaleString(undefined, {maximumFractionDigits: 0})} → €{(resultsData.end_equity_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}, 
                                <span style={{ color: (resultsData.end_equity_eur - resultsData.start_equity_eur) >= 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>
                                  {(resultsData.end_equity_eur - resultsData.start_equity_eur) >= 0 ? ' +' : ' '}€{(resultsData.end_equity_eur - resultsData.start_equity_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                </span>)
                              </span>
                            )}
                          </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Benchmark (Buy-and-Hold) Total Return">
                          <span>
                            {twrSummary?.benchmark_twr != null 
                              ? `${(twrSummary.benchmark_twr * 100).toFixed(2)}% (TWR)` 
                              : (performanceMetrics?.benchmarkReturn != null ? `${(performanceMetrics.benchmarkReturn * 100).toFixed(2)}%` : '-')}
                          </span>
                        </Descriptions.Item>
                        <Descriptions.Item label="Excess Return (vs Buy-and-Hold)">
                          <span style={{ color: (twrSummary?.excess_twr ?? performanceMetrics?.excessReturn) >= 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>
                            {twrSummary?.excess_twr != null
                              ? `${(twrSummary.excess_twr * 100).toFixed(2)}% (TWR)`
                              : (performanceMetrics?.excessReturn != null ? `${(performanceMetrics.excessReturn * 100).toFixed(2)}%` : '-')}
                          </span>
                        </Descriptions.Item>
                        {/* DAS-2344: Max drawdown — peak-to-trough on strategy equity.
                            Persisted in backtest_runs.max_dd_pct since this ticket; NULL
                            for older runs (no historical backfill per user decision). */}
                        <Descriptions.Item label="Max Drawdown">
                          <span style={{ color: (runInfo?.max_dd_pct != null && Number(runInfo.max_dd_pct) < 0) ? '#cf1322' : '#999', fontWeight: 'bold' }}>
                            {runInfo?.max_dd_pct != null
                              ? `${Number(runInfo.max_dd_pct).toFixed(2)}%`
                              : '-'}
                          </span>
                        </Descriptions.Item>
                        {/* DAS-1659: Secondary Benchmark */}
                        {resultsData?.secondary_benchmark_label && (
                          <>
                            <Descriptions.Item label={`Secondary Benchmark (${resultsData.secondary_benchmark_label}) Total Return`}>
                              <span>
                                {twrSummary?.secondary_benchmark_twr != null 
                                  ? `${(twrSummary.secondary_benchmark_twr * 100).toFixed(2)}% (TWR)` 
                                  : '-'}
                              </span>
                            </Descriptions.Item>
                            <Descriptions.Item label={`Excess Return (vs ${resultsData.secondary_benchmark_label})`}>
                              <span style={{ color: (twrSummary?.secondary_excess_twr ?? 0) >= 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>
                                {twrSummary?.secondary_excess_twr != null 
                                  ? `${(twrSummary.secondary_excess_twr * 100).toFixed(2)}% (TWR)` 
                                  : '-'}
                              </span>
                            </Descriptions.Item>
                          </>
                        )}
                      </Descriptions>
                    </div>
                    
                    {/* DAS-1751: FX Impact moved here, below Performance Summary */}
                    {fxSummary && (
                      <div style={{ 
                        flex: '0 0 auto',
                        padding: '12px 16px', 
                        background: '#f0f5ff', 
                        borderRadius: '8px', 
                        border: '1px solid #adc6ff',
                        alignSelf: 'flex-start'
                      }}>
                        <Typography.Text strong style={{ fontSize: 13 }}>FX Impact (EUR/USD)</Typography.Text>
                        <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                          {`EUR value of 1 USD: ${(fxSummary.eurusd_return * 100).toFixed(2)}% change`}
                        </div>
                      </div>
                    )}
                    
                    {/* DAS-1760: Trade Statistics */}
                    {eventsList && eventsList.length > 0 && (() => {
                      // Calculate trade statistics
                      const trades = eventsList.filter(e => e.event_type === 'trade');
                      const buys = trades.filter(t => t.qty > 0);
                      const sells = trades.filter(t => t.qty < 0);
                      const distinctTickers = [...new Set(trades.map(t => t.ticker))];
                      
                      // Calculate per-ticker P&L (realized from completed round-trips)
                      const tickerPnL = {};
                      trades.forEach(t => {
                        const ticker = t.ticker;
                        if (!tickerPnL[ticker]) {
                          tickerPnL[ticker] = { value: 0, qty: 0 };
                        }
                        // For sells (qty < 0), we receive cash: -qty * price
                        // For buys (qty > 0), we spend cash: -qty * price
                        tickerPnL[ticker].value += -(t.qty * t.price);
                        tickerPnL[ticker].qty += t.qty;
                      });
                      
                      // Find biggest winner and bleeder (by net cash flow)
                      const tickerResults = Object.entries(tickerPnL)
                        .map(([ticker, data]) => ({ ticker, pnl: data.value, qty: data.qty }))
                        .sort((a, b) => b.pnl - a.pnl);
                      
                      const biggestWinner = tickerResults.length > 0 ? tickerResults[0] : null;
                      const biggestBleeder = tickerResults.length > 0 ? tickerResults[tickerResults.length - 1] : null;
                      
                      return (
                        <div style={{ 
                          flex: '0 0 auto',
                          padding: '12px 16px', 
                          background: '#f6ffed', 
                          borderRadius: '8px', 
                          border: '1px solid #b7eb8f',
                          alignSelf: 'flex-start',
                          minWidth: '200px'
                        }}>
                          <Typography.Text strong style={{ fontSize: 13 }}>Trade Statistics</Typography.Text>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                            <div>Buys: {buys.length} | Sells: {sells.length} | Total: {trades.length}</div>
                            <div>Distinct tickers: {distinctTickers.length}</div>
                            {biggestWinner && biggestWinner.pnl > 0 && (
                              <div style={{ color: '#52c41a', marginTop: 4 }}>
                                🏆 Winner: {biggestWinner.ticker} (+€{biggestWinner.pnl.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
                              </div>
                            )}
                            {biggestBleeder && biggestBleeder.pnl < 0 && (
                              <div style={{ color: '#f5222d' }}>
                                📉 Bleeder: {biggestBleeder.ticker} (€{biggestBleeder.pnl.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })})
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* DAS-1857: Regime Performance Analysis */}
                {regimePerformance && regimePerformance.regime_performance && (
                  <div style={{ 
                    padding: '16px', 
                    background: '#fff',
                    border: '1px solid #e8e8e8', 
                    borderRadius: '8px',
                    marginBottom: '16px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                      📊 Regime Performance Analysis
                      {regimePerformance.summary?.regime_enabled && (
                        <Tag color="green" style={{ marginLeft: 8, fontWeight: 'normal' }}>Regime Adjustment Enabled</Tag>
                      )}
                    </Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                      VIX thresholds: Bull &lt;{regimePerformance.summary?.vix_bull_threshold}, Bear &gt;{regimePerformance.summary?.vix_bear_threshold} | 
                      Total trading days: {regimePerformance.summary?.total_days}
                    </Typography.Text>
                    
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {/* BULL Regime */}
                      <div style={{ 
                        flex: '1 1 200px',
                        padding: '12px', 
                        background: '#f6ffed', 
                        borderRadius: '6px',
                        border: '1px solid #b7eb8f'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#52c41a', marginBottom: 8, fontSize: 14 }}>
                          🐂 BULL ({regimePerformance.regime_performance.BULL?.days || 0} days)
                        </div>
                        <div style={{ fontSize: 12 }}>
                          <div>Total Return: <span style={{ fontWeight: 'bold', color: regimePerformance.regime_performance.BULL?.total_return_pct >= 0 ? '#52c41a' : '#cf1322' }}>
                            {regimePerformance.regime_performance.BULL?.total_return_pct >= 0 ? '+' : ''}{regimePerformance.regime_performance.BULL?.total_return_pct?.toFixed(2) || '0.00'}%
                          </span></div>
                          <div>Avg Daily: {regimePerformance.regime_performance.BULL?.avg_daily_return_bps?.toFixed(1) || '0.0'} bps</div>
                          <div style={{ marginTop: 6, fontSize: 11 }}>
                            <div>vs B&H: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.BULL?.wins_vs_bh || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.BULL?.losses_vs_bh || 0}L</span> ({regimePerformance.regime_performance.BULL?.win_rate_vs_bh || 0}%)</div>
                            <div>vs {regimePerformance.summary?.secondary_benchmark}: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.BULL?.wins_vs_secondary || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.BULL?.losses_vs_secondary || 0}L</span> ({regimePerformance.regime_performance.BULL?.win_rate_vs_secondary || 0}%)</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* NEUTRAL Regime */}
                      <div style={{ 
                        flex: '1 1 200px',
                        padding: '12px', 
                        background: '#fffbe6', 
                        borderRadius: '6px',
                        border: '1px solid #ffe58f'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#faad14', marginBottom: 8, fontSize: 14 }}>
                          ⚖️ NEUTRAL ({regimePerformance.regime_performance.NEUTRAL?.days || 0} days)
                        </div>
                        <div style={{ fontSize: 12 }}>
                          <div>Total Return: <span style={{ fontWeight: 'bold', color: regimePerformance.regime_performance.NEUTRAL?.total_return_pct >= 0 ? '#52c41a' : '#cf1322' }}>
                            {regimePerformance.regime_performance.NEUTRAL?.total_return_pct >= 0 ? '+' : ''}{regimePerformance.regime_performance.NEUTRAL?.total_return_pct?.toFixed(2) || '0.00'}%
                          </span></div>
                          <div>Avg Daily: {regimePerformance.regime_performance.NEUTRAL?.avg_daily_return_bps?.toFixed(1) || '0.0'} bps</div>
                          <div style={{ marginTop: 6, fontSize: 11 }}>
                            <div>vs B&H: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.NEUTRAL?.wins_vs_bh || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.NEUTRAL?.losses_vs_bh || 0}L</span> ({regimePerformance.regime_performance.NEUTRAL?.win_rate_vs_bh || 0}%)</div>
                            <div>vs {regimePerformance.summary?.secondary_benchmark}: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.NEUTRAL?.wins_vs_secondary || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.NEUTRAL?.losses_vs_secondary || 0}L</span> ({regimePerformance.regime_performance.NEUTRAL?.win_rate_vs_secondary || 0}%)</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* BEAR Regime */}
                      <div style={{ 
                        flex: '1 1 200px',
                        padding: '12px', 
                        background: '#fff2f0', 
                        borderRadius: '6px',
                        border: '1px solid #ffccc7'
                      }}>
                        <div style={{ fontWeight: 'bold', color: '#cf1322', marginBottom: 8, fontSize: 14 }}>
                          🐻 BEAR ({regimePerformance.regime_performance.BEAR?.days || 0} days)
                        </div>
                        <div style={{ fontSize: 12 }}>
                          <div>Total Return: <span style={{ fontWeight: 'bold', color: regimePerformance.regime_performance.BEAR?.total_return_pct >= 0 ? '#52c41a' : '#cf1322' }}>
                            {regimePerformance.regime_performance.BEAR?.total_return_pct >= 0 ? '+' : ''}{regimePerformance.regime_performance.BEAR?.total_return_pct?.toFixed(2) || '0.00'}%
                          </span></div>
                          <div>Avg Daily: {regimePerformance.regime_performance.BEAR?.avg_daily_return_bps?.toFixed(1) || '0.0'} bps</div>
                          <div style={{ marginTop: 6, fontSize: 11 }}>
                            <div>vs B&H: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.BEAR?.wins_vs_bh || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.BEAR?.losses_vs_bh || 0}L</span> ({regimePerformance.regime_performance.BEAR?.win_rate_vs_bh || 0}%)</div>
                            <div>vs {regimePerformance.summary?.secondary_benchmark}: <span style={{ color: '#52c41a' }}>{regimePerformance.regime_performance.BEAR?.wins_vs_secondary || 0}W</span> / <span style={{ color: '#cf1322' }}>{regimePerformance.regime_performance.BEAR?.losses_vs_secondary || 0}L</span> ({regimePerformance.regime_performance.BEAR?.win_rate_vs_secondary || 0}%)</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* DAS-1707: Removed confusing "Overlap-only valuation" message */}
                
                {/* DAS-1708: Configuration Summary */}
                {runInfo?.config && (
                  <div style={{ 
                    padding: '12px 16px', 
                    background: '#fafafa',
                    border: '1px solid #e8e8e8', 
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>
                      Configuration Summary
                    </Typography.Title>
                    {(() => {
                      // Extract rebalancing config from constraints_json._ui_extras
                      const cfg = runInfo.config;
                      let constraints = cfg?.constraints_json;
                      if (typeof constraints === 'string') {
                        try { constraints = JSON.parse(constraints); } catch { constraints = {}; }
                      }
                      const uiExtras = constraints?._ui_extras || {};
                      const rebalancing = cfg?.rebalancing || uiExtras.rebalancing || {};
                      const liquidityFilters = cfg?.liquidity_filters || uiExtras.liquidity_filters || {};
                      
                      // Parse cash policy and position sizing
                      let cashPolicy = cfg?.cash_policy || cfg?.cash_policy_json;
                      if (typeof cashPolicy === 'string') {
                        try { cashPolicy = JSON.parse(cashPolicy); } catch { cashPolicy = {}; }
                      }
                      let positionSizing = cfg?.position_sizing || cfg?.position_sizing_json;
                      if (typeof positionSizing === 'string') {
                        try { positionSizing = JSON.parse(positionSizing); } catch { positionSizing = {}; }
                      }
                      
                      // Format date helper
                      const formatDate = (d) => {
                        if (!d) return '-';
                        let dateStr = d;
                        if (typeof d === 'string') {
                          const match = d.match(/(\d{4})-(\d{2})-(\d{2})/);
                          if (match) dateStr = `${match[1]}-${match[2]}-${match[3]}`;
                        }
                        try {
                          const date = new Date(dateStr + 'T00:00:00');
                          if (isNaN(date.getTime())) return String(d).substring(0, 10);
                          return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                        } catch { return String(d).substring(0, 10); }
                      };
                      
                      // Determine scenario label
                      const getScenarioLabel = () => {
                        if (rebalancing?.enabled) {
                          const factor = rebalancing.factor_strategy || {};
                          const preset = factor.preset === 'value' ? 'Value' : factor.preset === 'growth' ? 'Growth' : 'Custom';
                          return `Rebalancing (${preset})`;
                        }
                        if (cfg?.backtest_clause_set_id) return `Clause Set #${cfg.backtest_clause_set_id}`;
                        if (cfg?.backtest_clause_ids?.length) return `Clauses (${cfg.backtest_clause_ids.length})`;
                        if (cfg?.alert_ids?.length) return `Alerts (${cfg.alert_ids.length})`;
                        return 'Buy & Hold';
                      };
                      
                      return (
                        <Descriptions column={{ xs: 1, sm: 2, md: 3, lg: 4 }} size="small">
                          <Descriptions.Item label="Period">
                            {formatDate(cfg.start_date)} → {formatDate(cfg.end_date)}
                          </Descriptions.Item>
                          <Descriptions.Item label="Scenario">
                            {getScenarioLabel()}
                          </Descriptions.Item>
                          {rebalancing?.enabled && (
                            <>
                              <Descriptions.Item label="Rebal Frequency">
                                {rebalancing.frequency_days} days
                              </Descriptions.Item>
                              <Descriptions.Item label="Sector Neutral">
                                {rebalancing.sector_neutral ? 'Yes' : 'No'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Selection">
                                Top {100 - (rebalancing.selection_percentile || 98)}%
                              </Descriptions.Item>
                              <Descriptions.Item label="Z-Score Baseline">
                                {rebalancing.z_score_baseline === 'market' ? 'Market' : 'Universe'}
                              </Descriptions.Item>
                              {!rebalancing.sector_neutral && (
                                <Descriptions.Item label="Max Sector Exp.">
                                  {rebalancing.max_sector_exposure_pct || 100}%
                                </Descriptions.Item>
                              )}
                            </>
                          )}
                          {liquidityFilters?.enabled && liquidityFilters?.min_market_cap_eur && (
                            <Descriptions.Item label="Min Market Cap">
                              €{(liquidityFilters.min_market_cap_eur / 1e9).toFixed(0)}B
                            </Descriptions.Item>
                          )}
                          <Descriptions.Item label="Cash Policy">
                            {cashPolicy?.reinvest_proceeds ? 'Reinvest' : 'Hold Cash'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Start Cash">
                            €{((positionSizing?.start_cash_eur || 200000) / 1000).toFixed(0)}K
                          </Descriptions.Item>
                        </Descriptions>
                      );
                    })()}
                  </div>
                )}

                {/* DAS-1712: Graphs moved up - show after Configuration Summary */}
                {/* DAS-1751: Full width graphs */}
                {/* DAS-1776: Build regime bands from period_performance */}
                {(() => {
                  const regimeBands = [];
                  const periodPerf = resultsData?.regime_summary?.period_performance || [];
                  const dates = cashData?.dates || [];
                  
                  if (periodPerf.length > 0 && dates.length > 0) {
                    // Create a date -> index map for fast lookup
                    const dateIndexMap = {};
                    dates.forEach((d, i) => { dateIndexMap[d] = i; });
                    
                    // Build bands from period performance
                    for (let i = 0; i < periodPerf.length; i++) {
                      const period = periodPerf[i];
                      const startDate = period.start_date;
                      const endDate = period.end_date;
                      const regime = period.regime || 'NEUTRAL';  // DAS-1776: field is 'regime' not 'detected_regime'
                      
                      // Find start index (exact or closest after)
                      let startIdx = dateIndexMap[startDate];
                      if (startIdx === undefined || startIdx === null) {
                        // Find closest date on or after startDate
                        for (let j = 0; j < dates.length; j++) {
                          if (dates[j] >= startDate) { startIdx = j; break; }
                        }
                        if (startIdx === undefined || startIdx === null) startIdx = 0;
                      }
                      
                      // Find end index (exact or closest before)
                      let endIdx = dateIndexMap[endDate];
                      if (endIdx === undefined || endIdx === null) {
                        // Find closest date on or before endDate
                        for (let j = dates.length - 1; j >= 0; j--) {
                          if (dates[j] <= endDate) { endIdx = j; break; }
                        }
                        if (endIdx === undefined || endIdx === null) endIdx = dates.length - 1;
                      }
                      
                      // Ensure valid indices
                      if (typeof startIdx === 'number' && typeof endIdx === 'number' && startIdx <= endIdx) {
                        regimeBands.push({
                          startIndex: startIdx,
                          endIndex: endIdx,
                          regime: regime
                        });
                      }
                    }
                  }
                  
                  return (
                    <div style={{ marginBottom: '24px' }}>
                      <Typography.Text strong>Net Cash (EUR)</Typography.Text>
                      {regimeBands.length > 0 && (
                        <span style={{ marginLeft: 12, fontSize: 10 }}>
                          <span style={{ background: 'rgba(82, 196, 26, 0.15)', padding: '2px 6px', borderRadius: 2 }}>🐂 BULL</span>
                          <span style={{ background: 'rgba(207, 19, 34, 0.15)', padding: '2px 6px', borderRadius: 2, marginLeft: 4 }}>🐻 BEAR</span>
                          <span style={{ background: 'rgba(22, 119, 255, 0.10)', padding: '2px 6px', borderRadius: 2, marginLeft: 4 }}>⚖️ NEUTRAL</span>
                        </span>
                      )}
                      <div>
                        <SvgLineChart
                          series={[cashData.vals]}
                          colors={['#cf1322']}
                          width={920}
                          height={200}
                          xLabels={cashData.dates}
                          transactions={cashTransactions}
                          regimeBands={regimeBands}
                        />
                      </div>
                    </div>
                  );
                })()}
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Text strong>Performance Comparison Graph</Typography.Text>
                  {overlayData.dates && overlayData.dates.length > 0 && (
                    <div style={{ marginBottom: 8, fontSize: 11 }}>
                      <span style={{ color: '#1677ff', fontWeight: 500 }}>●</span> Strategy &nbsp;
                      <span style={{ color: '#faad14', fontWeight: 500 }}>●</span> Buy-and-Hold
                      {/* DAS-1659: Show secondary benchmark legend if available */}
                      {resultsData?.secondary_benchmark_label && (
                        <>
                          &nbsp;
                          <span style={{ color: '#8c8c8c', fontWeight: 500 }}>●</span> {resultsData.secondary_benchmark_label}
                        </>
                      )}
                    </div>
                  )}
                  <div>
                    <SvgLineChart
                      series={
                        resultsData?.secondary_benchmark_label
                          ? [equityData?.values || [], benchmarkData?.values || [], secondaryBenchmarkData?.values || []]
                          : [equityData?.values || [], benchmarkData?.values || []]
                      }
                      colors={
                        resultsData?.secondary_benchmark_label
                          ? ['#1677ff', '#faad14', '#8c8c8c']
                          : ['#1677ff', '#faad14']
                      }
                      width={920}
                      height={200}
                      xLabels={equityData?.dates || []}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <Typography.Text strong>Excess (Equity − Buy-and-Hold, pts)</Typography.Text>
                  <div>
                    <SvgLineChart
                      series={[excessData.vals]}
                      colors={[excessLineColor]}
                      width={920}
                      height={300}
                      xLabels={excessData.dates}
                      transactions={excessTransactions}
                      dynamicColor={true}
                    />
                  </div>
                </div>

                {/* DAS-1773: Market Cap Distribution Analysis */}
                {eqCheck?.event_attribution?.market_cap_analysis && (
                  <div style={{ 
                    marginBottom: '24px', 
                    padding: '16px', 
                    background: '#f9f0ff', 
                    borderRadius: '8px', 
                    border: '1px solid #d3adf7' 
                  }}>
                    <Typography.Text strong style={{ color: '#722ed1', fontSize: 14 }}>
                      📊 Market Cap Distribution (Slippage Analysis)
                    </Typography.Text>
                    
                    {/* Summary Stats */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginTop: 12, marginBottom: 12 }}>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                          {eqCheck.event_attribution.market_cap_analysis.summary?.total_trades || 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total Trades</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                          €{Number(eqCheck.event_attribution.market_cap_analysis.summary?.total_volume_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total Volume</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#cf1322' }}>
                          €{Number(eqCheck.event_attribution.market_cap_analysis.summary?.total_slippage_eur || 0).toFixed(2)}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total Slippage</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#faad14' }}>
                          {Number(eqCheck.event_attribution.market_cap_analysis.summary?.weighted_avg_slippage_pct || 0).toFixed(3)}%
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Avg Slippage</div>
                      </div>
                      {/* DAS-1774: Total P&L */}
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ 
                          fontSize: 20, 
                          fontWeight: 'bold', 
                          color: (eqCheck.event_attribution.market_cap_analysis.summary?.total_pnl_eur || 0) >= 0 ? '#52c41a' : '#cf1322' 
                        }}>
                          {(eqCheck.event_attribution.market_cap_analysis.summary?.total_pnl_eur || 0) >= 0 ? '+' : ''}
                          €{Number(eqCheck.event_attribution.market_cap_analysis.summary?.total_pnl_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total P&L</div>
                      </div>
                    </div>

                    {/* By Tier Table */}
                    <div style={{ background: 'white', borderRadius: 6, padding: 8 }}>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #d3adf7' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Tier</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Trades</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Volume</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>% Vol</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Slippage</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Slip %</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>P&L</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>% P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {['large_cap', 'mid_cap', 'small_cap', 'unknown'].map(tier => {
                            const data = eqCheck.event_attribution.market_cap_analysis.by_tier?.[tier];
                            if (!data || data.trade_count === 0) return null;
                            const tierLabels = {
                              large_cap: { icon: '🔵', label: 'Large Cap', color: '#1890ff' },
                              mid_cap: { icon: '🟡', label: 'Mid Cap', color: '#faad14' },
                              small_cap: { icon: '🔴', label: 'Small Cap', color: '#cf1322' },
                              unknown: { icon: '⚪', label: 'Unknown', color: '#666' }
                            };
                            const t = tierLabels[tier];
                            const pnlColor = (data.pnl_eur || 0) >= 0 ? '#52c41a' : '#cf1322';
                            return (
                              <tr key={tier} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '6px 8px' }}>{t.icon} {t.label}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px' }}>{data.trade_count}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px' }}>€{Number(data.volume_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: t.color, fontWeight: 'bold' }}>{data.volume_pct}%</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: '#cf1322' }}>€{Number(data.slippage_eur).toFixed(2)}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px' }}>{data.implied_slippage_pct}%</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: pnlColor, fontWeight: 'bold' }}>
                                  {(data.pnl_eur || 0) >= 0 ? '+' : ''}€{Number(data.pnl_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                </td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', color: pnlColor, fontWeight: 'bold' }}>{data.pnl_pct || 0}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Thresholds */}
                    <div style={{ fontSize: 10, color: '#666', marginTop: 8 }}>
                      <strong>Thresholds:</strong> Large Cap ≥ €{(eqCheck.event_attribution.market_cap_analysis.thresholds?.large_cap_min_eur / 1e9).toFixed(0)}B | 
                      Mid Cap ≥ €{(eqCheck.event_attribution.market_cap_analysis.thresholds?.mid_cap_min_eur / 1e9).toFixed(0)}B
                    </div>
                  </div>
                )}

                {/* DAS-1776: Currency/Country Summary */}
                {/* DEBUG: Show if data exists */}
                {!resultsData?.currency_summary && (
                  <div style={{ padding: 8, background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, marginBottom: 16 }}>
                    ⚠️ <strong>Currency Summary not available</strong> - Flask backend needs restart to load new code.
                    <br/>
                    <small>Keys in resultsData: {Object.keys(resultsData || {}).join(', ')}</small>
                  </div>
                )}
                {resultsData?.currency_summary && (
                  <div style={{ 
                    marginBottom: '24px', 
                    padding: '16px', 
                    background: '#f0f9ff', 
                    borderRadius: '8px', 
                    border: '1px solid #91caff' 
                  }}>
                    <Typography.Text strong style={{ color: '#1677ff', fontSize: 14 }}>
                      🌍 Currency/Country Overview
                    </Typography.Text>
                    
                    {/* Summary stats */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginTop: 12, marginBottom: 12 }}>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1677ff' }}>
                          {resultsData.currency_summary.summary?.total_currencies || 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Currencies</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1677ff' }}>
                          {resultsData.currency_summary.summary?.total_tickers || 0}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Unique Tickers</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1677ff' }}>
                          {(resultsData.currency_summary.summary?.total_trades || 0).toLocaleString()}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total Trades</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 16px' }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                          €{(resultsData.currency_summary.summary?.total_dividend_income_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div style={{ fontSize: 11, color: '#666' }}>Total Dividends</div>
                      </div>
                    </div>

                    {/* Table */}
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#e6f4ff' }}>
                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #91caff' }}>Currency</th>
                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #91caff' }}>Tickers</th>
                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #91caff' }}>Trades</th>
                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #91caff' }}>Dividends (€)</th>
                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #91caff' }}>Holdings (€)</th>
                            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #91caff' }}>Net Result (€)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(resultsData.currency_summary.by_currency || []).map((row, idx) => (
                            <tr key={row.currency} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
                                {row.currency === 'USD' ? '🇺🇸' : row.currency === 'EUR' ? '🇪🇺' : row.currency === 'GBP' || row.currency === 'GBX' || row.currency === 'GBP' ? '🇬🇧' : row.currency === 'JPY' ? '🇯🇵' : row.currency === 'CHF' ? '🇨🇭' : row.currency === 'CAD' ? '🇨🇦' : row.currency === 'AUD' ? '🇦🇺' : row.currency === 'HKD' ? '🇭🇰' : '🌐'} {row.currency}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {row.ticker_count}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                {row.trade_count.toLocaleString()}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0', color: '#52c41a' }}>
                                €{(row.dividend_income_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #f0f0f0', color: '#1677ff' }}>
                                €{(row.holdings_value_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                              <td style={{ 
                                textAlign: 'right', 
                                padding: '6px 8px', 
                                borderBottom: '1px solid #f0f0f0',
                                fontWeight: 'bold',
                                color: (row.net_result_eur || 0) >= 0 ? '#52c41a' : '#cf1322'
                              }}>
                                {(row.net_result_eur || 0) >= 0 ? '+' : ''}€{(row.net_result_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                            </tr>
                          ))}
                          {/* Total row */}
                          <tr style={{ background: '#e6f4ff', fontWeight: 'bold' }}>
                            <td style={{ padding: '8px', borderTop: '2px solid #91caff' }}>Total</td>
                            <td style={{ textAlign: 'right', padding: '8px', borderTop: '2px solid #91caff' }}>
                              {resultsData.currency_summary.summary?.total_tickers || 0}
                            </td>
                            <td style={{ textAlign: 'right', padding: '8px', borderTop: '2px solid #91caff' }}>
                              {(resultsData.currency_summary.summary?.total_trades || 0).toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'right', padding: '8px', borderTop: '2px solid #91caff', color: '#52c41a' }}>
                              €{(resultsData.currency_summary.summary?.total_dividend_income_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </td>
                            <td style={{ textAlign: 'right', padding: '8px', borderTop: '2px solid #91caff', color: '#1677ff' }}>
                              €{(resultsData.currency_summary.by_currency || []).reduce((sum, r) => sum + (r.holdings_value_eur || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </td>
                            <td style={{ 
                              textAlign: 'right', 
                              padding: '8px', 
                              borderTop: '2px solid #91caff',
                              color: (resultsData.currency_summary.summary?.total_net_result_eur || 0) >= 0 ? '#52c41a' : '#cf1322'
                            }}>
                              {(resultsData.currency_summary.summary?.total_net_result_eur || 0) >= 0 ? '+' : ''}€{(resultsData.currency_summary.summary?.total_net_result_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* DAS-1712: Portfolio Summary moved after graphs */}
                {effectivePortfolioSummary && (
                  <div>
                    <Space>
                      <Typography.Text strong>Portfolio Summary</Typography.Text>
                      {twrSummary?.strategy_twr != null && (
                        <Typography.Text style={{ 
                          color: twrSummary.strategy_twr >= 0 ? '#52c41a' : '#cf1322',
                          fontWeight: 600
                        }}>
                          {twrSummary.strategy_twr >= 0 ? '+' : ''}{(twrSummary.strategy_twr * 100).toFixed(2)}% (TWR)
                        </Typography.Text>
                      )}
                    </Space>
                    <div style={{ marginTop: 8, marginBottom: 16 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {`Start: ${effectivePortfolioSummary.start_date || 'n/a'} | End: ${effectivePortfolioSummary.end_date || 'n/a'}`}
                      </Typography.Text>
                    </div>
                    {(() => {
                      // Calculate totals across ALL data (not just current page)
                      const allTickers = effectivePortfolioSummary.tickers || [];
                      const grandTotalDividends = allTickers.reduce((sum, r) => sum + (r.dividends_eur || 0), 0);
                      const grandTotalResult = allTickers.reduce((sum, r) => sum + (r.net_result_eur || 0), 0);
                      const grandTotalBuys = allTickers.reduce((sum, r) => sum + (r.buy_count || 0), 0);
                      const grandTotalSells = allTickers.reduce((sum, r) => sum + (r.sell_count || 0), 0);
                      
                      // DAS-1714: Handle row click to show ticker events detail
                      const handleTickerRowClick = async (record) => {
                        const tickerEvents = (eventsList || [])
                          .filter(ev => ev.ticker === record.ticker)
                          .map(ev => ({
                            ...ev,
                            trade_value: (ev.event_type === 'trade' || ev.event_type === 'dividend') 
                              ? Math.abs(Number(ev.qty) || 0) * (Number(ev.price) || 0) 
                              : null
                          }));
                        
                        // Set modal open immediately with loading state for sector
                        setTickerDetailModal({ 
                          open: true, 
                          ticker: record.ticker, 
                          data: tickerEvents,
                          summary: record,
                          sector: null
                        });
                        
                        // Fetch sector info
                        try {
                          const res = await callAction('get_ticker_sector', { ticker: record.ticker }, { useLoader: false });
                          console.log('get_ticker_sector response:', res);
                          if (res?.success && res.data?.sector) {
                            setTickerDetailModal(prev => ({ 
                              ...prev, 
                              sector: res.data.sector,
                              industry: res.data.industry || ''
                            }));
                          }
                        } catch (err) {
                          console.error('get_ticker_sector error:', err);
                        }
                      };
                      
                      return (
                        <Table
                          dataSource={allTickers}
                          size="small"
                          pagination={{ pageSize: 10 }}
                          rowKey="ticker"
                          onRow={(record) => ({
                            onClick: () => handleTickerRowClick(record),
                            style: { cursor: 'pointer' }
                          })}
                          columns={[
                            { 
                              title: 'Ticker', 
                              dataIndex: 'ticker', 
                              key: 'ticker',
                              sorter: (a, b) => a.ticker.localeCompare(b.ticker),
                              // DAS-1778: Make ticker clickable to open SearchResultModal
                              render: (ticker) => (
                                <span 
                                  style={{ 
                                    cursor: 'pointer', 
                                    color: '#1890ff',
                                    fontWeight: 500
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click from also firing
                                    setSearchResultModal({ open: true, ticker });
                                  }}
                                >
                                  {renderTickerWithInfo(ticker)}
                                </span>
                              ),
                            },
                            { 
                              title: 'Start Qty', 
                              dataIndex: 'start_qty', 
                              key: 'start_qty',
                              sorter: (a, b) => (a.start_qty || 0) - (b.start_qty || 0),
                              render: (val, r) => (
                                <span>
                                  {val?.toFixed(2)}
                                  {r.was_initial && <span style={{ marginLeft: 4, fontSize: 10, color: '#1890ff' }}>(Init)</span>}
                                </span>
                              )
                            },
                            { 
                              title: 'End Qty', 
                              dataIndex: 'end_qty', 
                              key: 'end_qty',
                              sorter: (a, b) => (a.end_qty || 0) - (b.end_qty || 0),
                              render: (val) => val?.toFixed(2)
                            },
                            { 
                              title: 'Dividends (€)', 
                              dataIndex: 'dividends_eur', 
                              key: 'dividends_eur',
                              sorter: (a, b) => (a.dividends_eur || 0) - (b.dividends_eur || 0),
                              render: (val) => val > 0 ? <span style={{ color: '#52c41a' }}>+€{val.toFixed(2)}</span> : '-'
                            },
                            { 
                              title: 'Activity', 
                              key: 'activity',
                              sorter: (a, b) => ((a.buy_count || 0) + (a.sell_count || 0)) - ((b.buy_count || 0) + (b.sell_count || 0)),
                              render: (_, r) => {
                                const parts = [];
                                if (r.buy_count > 0) parts.push(<span key="b" style={{ color: '#1890ff' }}>{r.buy_count} Buys</span>);
                                if (r.sell_count > 0) parts.push(<span key="s" style={{ color: '#cf1322' }}>{r.sell_count} Sells</span>);
                                return <Space>{parts}</Space>;
                              }
                            },
                            {
                              // DAS-1714: Net result per ticker (cash flow basis)
                              title: 'Result (€)',
                              dataIndex: 'net_result_eur',
                              key: 'net_result_eur',
                              sorter: (a, b) => (a.net_result_eur || 0) - (b.net_result_eur || 0),
                              defaultSortOrder: 'descend',
                              render: (val, r) => {
                                if (val == null) return '-';
                                const isPositive = val >= 0;
                                const hasOpenPosition = r.end_qty > 0;
                                return (
                                  <span style={{ 
                                    color: isPositive ? '#52c41a' : '#cf1322',
                                    fontWeight: 500
                                  }}>
                                    {isPositive ? '+' : ''}€{val.toFixed(2)}
                                    {hasOpenPosition && <span style={{ marginLeft: 4, fontSize: 10, color: '#8c8c8c' }}>(open)</span>}
                                  </span>
                                );
                              }
                            }
                          ]}
                          summary={() => (
                            <Table.Summary fixed>
                              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                                <Table.Summary.Cell index={0}>TOTAL ({allTickers.length} tickers)</Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>-</Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>-</Table.Summary.Cell>
                                <Table.Summary.Cell index={3}>
                                  {grandTotalDividends > 0 ? (
                                    <span style={{ color: '#52c41a' }}>+€{grandTotalDividends.toFixed(2)}</span>
                                  ) : '-'}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4}>
                                  <Space>
                                    {grandTotalBuys > 0 && <span style={{ color: '#1890ff' }}>{grandTotalBuys} Buys</span>}
                                    {grandTotalSells > 0 && <span style={{ color: '#cf1322' }}>{grandTotalSells} Sells</span>}
                                  </Space>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                  <span style={{ 
                                    color: grandTotalResult >= 0 ? '#52c41a' : '#cf1322',
                                    fontWeight: 700
                                  }}>
                                    {grandTotalResult >= 0 ? '+' : ''}€{grandTotalResult.toFixed(2)}
                                  </span>
                                </Table.Summary.Cell>
                              </Table.Summary.Row>
                            </Table.Summary>
                          )}
                        />
                      );
                    })()}
                  </div>
                )}

                {/* DAS-1716: Sector Overview after Portfolio Summary */}
                {sectorOverview && sectorOverview.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Typography.Text strong>Sector Overview</Typography.Text>
                    <div style={{ marginTop: 8, marginBottom: 4 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Results exclude sell proceeds from positions held at start (to avoid distortion)
                      </Typography.Text>
                    </div>
                    <Table
                      dataSource={sectorOverview}
                      rowKey="sector"
                      size="small"
                      pagination={false}
                      columns={[
                        {
                          title: 'Sector',
                          dataIndex: 'sector',
                          key: 'sector',
                          width: 150,
                          render: (val) => <strong>{val}</strong>
                        },
                        {
                          title: 'Tickers',
                          dataIndex: 'unique_tickers',
                          key: 'unique_tickers',
                          width: 80,
                          align: 'center',
                        },
                        {
                          title: 'Buys',
                          dataIndex: 'buy_count',
                          key: 'buy_count',
                          width: 70,
                          align: 'center',
                        },
                        {
                          title: 'Sells',
                          dataIndex: 'sell_count',
                          key: 'sell_count',
                          width: 70,
                          align: 'center',
                        },
                        {
                          title: 'Net Result (€)',
                          dataIndex: 'net_result_eur',
                          key: 'net_result_eur',
                          width: 120,
                          align: 'right',
                          render: (val) => {
                            if (val == null) return '-';
                            const num = Number(val);
                            const color = num >= 0 ? '#52c41a' : '#cf1322';
                            const sign = num >= 0 ? '+' : '';
                            return <span style={{ color, fontWeight: 'bold' }}>{sign}€{num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                          }
                        },
                      ]}
                      summary={() => {
                        const totalTickers = sectorOverview.reduce((sum, r) => sum + r.unique_tickers, 0);
                        const totalBuys = sectorOverview.reduce((sum, r) => sum + r.buy_count, 0);
                        const totalSells = sectorOverview.reduce((sum, r) => sum + r.sell_count, 0);
                        const totalResult = sectorOverview.reduce((sum, r) => sum + r.net_result_eur, 0);
                        const color = totalResult >= 0 ? '#52c41a' : '#cf1322';
                        const sign = totalResult >= 0 ? '+' : '';
                        return (
                          <Table.Summary fixed>
                            <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                              <Table.Summary.Cell index={0}>TOTAL</Table.Summary.Cell>
                              <Table.Summary.Cell index={1} align="center">{totalTickers}</Table.Summary.Cell>
                              <Table.Summary.Cell index={2} align="center">{totalBuys}</Table.Summary.Cell>
                              <Table.Summary.Cell index={3} align="center">{totalSells}</Table.Summary.Cell>
                              <Table.Summary.Cell index={4} align="right">
                                <span style={{ color, fontWeight: 'bold' }}>{sign}€{totalResult.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          </Table.Summary>
                        );
                      }}
                    />
                  </div>
                )}

                {/* DAS-1763: Display regime summary for the entire run */}
                {resultsData?.regime_summary && (
                  <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Typography.Text strong>📈 Regime Adjustment Summary</Typography.Text>
                        <div style={{ marginTop: 8 }}>
                          <Space size="large">
                            <span>
                              <Tag color="green">🐂 BULL</Tag>
                              <strong>{resultsData.regime_summary.regime_counts?.BULL || 0}</strong>
                            </span>
                            <span>
                              <Tag color="red">🐻 BEAR</Tag>
                              <strong>{resultsData.regime_summary.regime_counts?.BEAR || 0}</strong>
                            </span>
                            <span>
                              <Tag color="blue">⚖️ NEUTRAL</Tag>
                              <strong>{resultsData.regime_summary.regime_counts?.NEUTRAL || 0}</strong>
                            </span>
                          </Space>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: '#666' }}>
                          {resultsData.regime_summary.total_rebalancing_dates} rebalancing dates
                        </div>
                        <div style={{ fontSize: 11, color: '#fa8c16', fontWeight: 500 }}>
                          {resultsData.regime_summary.regime_changes?.length || 0} regime changes
                        </div>
                      </div>
                    </div>
                    
                    {/* DAS-1774: Regime Stats Table (P&L per regime) */}
                    {resultsData.regime_summary.regime_stats && (
                      <div style={{ marginTop: 12, background: '#fff', borderRadius: 6, padding: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>📊 Performance by Regime:</div>
                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #ffd591' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Regime</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}># Detections</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}># Rebalances</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}>P&L</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px' }}>% of Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {['BULL', 'BEAR', 'NEUTRAL'].map(regime => {
                              const stats = resultsData.regime_summary.regime_stats?.[regime];
                              const counts = resultsData.regime_summary.regime_counts?.[regime] || 0;
                              if (!stats && !counts) return null;
                              const regimeLabels = {
                                BULL: { icon: '🐂', label: 'BULL', color: '#52c41a', tagColor: 'green' },
                                BEAR: { icon: '🐻', label: 'BEAR', color: '#cf1322', tagColor: 'red' },
                                NEUTRAL: { icon: '⚖️', label: 'NEUTRAL', color: '#1890ff', tagColor: 'blue' }
                              };
                              const r = regimeLabels[regime];
                              const pnl = stats?.pnl_eur || 0;
                              const pnlColor = pnl >= 0 ? '#52c41a' : '#cf1322';
                              return (
                                <tr key={regime} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ padding: '6px 8px' }}>
                                    <Tag color={r.tagColor} style={{ margin: 0 }}>{r.icon} {r.label}</Tag>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px' }}>{counts}</td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px' }}>{stats?.rebalances || 0}</td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', color: pnlColor, fontWeight: 'bold' }}>
                                    {pnl >= 0 ? '+' : ''}€{Number(pnl).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', color: pnlColor, fontWeight: 'bold' }}>
                                    {stats?.pnl_pct || 0}%
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Total row */}
                            <tr style={{ borderTop: '2px solid #ffd591', background: '#fffbe6' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>Total</td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>
                                {resultsData.regime_summary.total_rebalancing_dates || 0}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>
                                {(resultsData.regime_summary.regime_stats?.BULL?.rebalances || 0) + 
                                 (resultsData.regime_summary.regime_stats?.BEAR?.rebalances || 0) + 
                                 (resultsData.regime_summary.regime_stats?.NEUTRAL?.rebalances || 0)}
                              </td>
                              <td style={{ 
                                textAlign: 'right', 
                                padding: '6px 8px', 
                                fontWeight: 'bold',
                                color: (resultsData.regime_summary.total_pnl_eur || 0) >= 0 ? '#52c41a' : '#cf1322'
                              }}>
                                {(resultsData.regime_summary.total_pnl_eur || 0) >= 0 ? '+' : ''}
                                €{Number(resultsData.regime_summary.total_pnl_eur || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                              </td>
                              <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>100%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}

                    {resultsData.regime_summary.regime_changes?.length > 0 && (
                      <div style={{ marginTop: 12, padding: 8, background: '#fff', borderRadius: 4, maxHeight: 100, overflowY: 'auto' }}>
                        <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>Regime Changes:</div>
                        {resultsData.regime_summary.regime_changes.map((change, idx) => (
                          <div key={idx} style={{ fontSize: 11, color: '#666' }}>
                            {change.date}: {change.from_regime} → {change.to_regime}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* DAS-1772: Per-period performance with benchmark comparison */}
                    {resultsData.regime_summary.period_performance?.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8 }}>📊 Performance by Period:</div>
                        <div style={{ maxHeight: 250, overflowY: 'auto', background: '#fff', borderRadius: 4, border: '1px solid #eee' }}>
                          <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #eee' }}>Period</th>
                                <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #eee' }}>Regime</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>Strategy</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>MSCI</th>
                                <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #eee' }}>Excess</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resultsData.regime_summary.period_performance.map((period, idx) => (
                                <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ fontFamily: 'monospace' }}>{period.start_date?.slice(5)}</span>
                                    <span style={{ color: '#999' }}> → </span>
                                    <span style={{ fontFamily: 'monospace' }}>{period.end_date?.slice(5)}</span>
                                    <span style={{ color: '#999', marginLeft: 4 }}>({period.days}d)</span>
                                  </td>
                                  <td style={{ padding: '4px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                    <Tag 
                                      color={period.regime === 'BULL' ? 'green' : period.regime === 'BEAR' ? 'red' : 'blue'} 
                                      style={{ fontSize: 9, padding: '0 4px', margin: 0 }}
                                    >
                                      {period.regime === 'BULL' ? '🐂' : period.regime === 'BEAR' ? '🐻' : '⚖️'}
                                    </Tag>
                                  </td>
                                  <td style={{ 
                                    padding: '4px 8px', 
                                    textAlign: 'right', 
                                    borderBottom: '1px solid #f0f0f0',
                                    color: period.strategy_return >= 0 ? '#52c41a' : '#ff4d4f',
                                    fontWeight: 500
                                  }}>
                                    {period.strategy_return >= 0 ? '+' : ''}{period.strategy_return?.toFixed(1)}%
                                  </td>
                                  <td style={{ 
                                    padding: '4px 8px', 
                                    textAlign: 'right', 
                                    borderBottom: '1px solid #f0f0f0',
                                    color: '#666'
                                  }}>
                                    {period.benchmark_return != null ? (
                                      <>{period.benchmark_return >= 0 ? '+' : ''}{period.benchmark_return?.toFixed(1)}%</>
                                    ) : '—'}
                                  </td>
                                  <td style={{ 
                                    padding: '4px 8px', 
                                    textAlign: 'right', 
                                    borderBottom: '1px solid #f0f0f0',
                                    fontWeight: 600,
                                    color: period.beat_benchmark ? '#52c41a' : period.beat_benchmark === false ? '#ff4d4f' : '#666'
                                  }}>
                                    {period.excess_return != null ? (
                                      <>
                                        {period.beat_benchmark ? '✓ ' : '✗ '}
                                        {period.excess_return >= 0 ? '+' : ''}{period.excess_return?.toFixed(1)}%
                                      </>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Summary stats */}
                        <div style={{ marginTop: 8, display: 'flex', gap: 16, fontSize: 10, color: '#666' }}>
                          <span>
                            Beat MSCI: <strong style={{ color: '#52c41a' }}>
                              {resultsData.regime_summary.period_performance.filter(p => p.beat_benchmark).length}
                            </strong> / {resultsData.regime_summary.period_performance.length} periods
                          </span>
                          <span>
                            Win Rate: <strong>
                              {Math.round(resultsData.regime_summary.period_performance.filter(p => p.beat_benchmark).length / resultsData.regime_summary.period_performance.length * 100)}%
                            </strong>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* DAS-1763: Display current detected regime (last rebalancing date) */}
                {filteredStocks && filteredStocks.detected_regime && !resultsData?.regime_summary && (
                  <div style={{ marginTop: 16, padding: 12, background: filteredStocks.detected_regime === 'BULL' ? '#f6ffed' : filteredStocks.detected_regime === 'BEAR' ? '#fff1f0' : '#f0f5ff', borderRadius: 8, border: `1px solid ${filteredStocks.detected_regime === 'BULL' ? '#b7eb8f' : filteredStocks.detected_regime === 'BEAR' ? '#ffa39e' : '#adc6ff'}` }}>
                    <Space>
                      <Typography.Text strong>
                        {filteredStocks.detected_regime === 'BULL' ? '🐂' : filteredStocks.detected_regime === 'BEAR' ? '🐻' : '⚖️'} Detected Regime:
                      </Typography.Text>
                      <Tag color={filteredStocks.detected_regime === 'BULL' ? 'green' : filteredStocks.detected_regime === 'BEAR' ? 'red' : 'blue'}>
                        {filteredStocks.detected_regime}
                      </Tag>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        (as of {filteredStocks.rebalancing_date})
                      </Typography.Text>
                    </Space>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {filteredStocks.detected_regime === 'BULL' && 'Factor weights adjusted for growth: lower ROA weight, higher P/B weight tolerance.'}
                      {filteredStocks.detected_regime === 'BEAR' && 'Factor weights adjusted for quality: higher ROA weight, stricter P/B requirements.'}
                      {filteredStocks.detected_regime === 'NEUTRAL' && 'Factor weights unchanged - market in neutral regime.'}
                    </div>
                  </div>
                )}

                {/* DAS-1762: Filtered Stocks by Momentum */}
                {filteredStocks && filteredStocks.filtered_count > 0 && (
                  <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 8, border: '1px solid #ffd591' }}>
                    <div 
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                      onClick={() => setFilteredStocksExpanded(!filteredStocksExpanded)}
                    >
                      <Space>
                        <Typography.Text strong>🚫 Filtered Stocks</Typography.Text>
                        <Tag color="orange">{filteredStocks.filtered_count} excluded</Tag>
                      </Space>
                      <Button type="link" size="small">
                        {filteredStocksExpanded ? '▲ Collapse' : '▼ Expand'}
                      </Button>
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Stocks excluded due to momentum filter or sector exclusion (rebalancing date: {filteredStocks.rebalancing_date})
                    </div>
                    
                    {filteredStocksExpanded && (
                      <div style={{ marginTop: 12 }}>
                        <Table
                          dataSource={filteredStocks.filtered_stocks}
                          rowKey="ticker"
                          size="small"
                          pagination={{ pageSize: 10, size: 'small' }}
                          columns={[
                            {
                              title: 'Ticker',
                              dataIndex: 'ticker',
                              key: 'ticker',
                              width: 80,
                              render: (ticker) => <strong>{ticker}</strong>
                            },
                            {
                              title: 'Sector',
                              dataIndex: 'sector',
                              key: 'sector',
                              width: 120,
                            },
                            {
                              title: 'Momentum',
                              dataIndex: 'momentum_value',
                              key: 'momentum_value',
                              width: 100,
                              align: 'right',
                              render: (val) => {
                                if (val == null) return <span style={{ color: '#999' }}>N/A</span>;
                                const pct = (val * 100).toFixed(1);
                                const color = val >= 0 ? '#52c41a' : '#cf1322';
                                return <span style={{ color, fontWeight: 500 }}>{val >= 0 ? '+' : ''}{pct}%</span>;
                              }
                            },
                            {
                              title: 'Filter Reason',
                              dataIndex: 'filtered_reason',
                              key: 'filtered_reason',
                              width: 180,
                              render: (reason) => {
                                const labels = {
                                  'momentum_below_threshold': '📉 Momentum too low',
                                  'no_momentum_data': '❓ No momentum data',
                                  'excluded_sector': '🚫 Excluded sector'
                                };
                                const colors = {
                                  'momentum_below_threshold': 'red',
                                  'no_momentum_data': 'default',
                                  'excluded_sector': 'orange'
                                };
                                return <Tag color={colors[reason] || 'default'}>{labels[reason] || reason}</Tag>;
                              }
                            },
                            {
                              title: 'ROA',
                              dataIndex: 'roa',
                              key: 'roa',
                              width: 70,
                              align: 'right',
                              render: (val) => val != null ? `${(val * 100).toFixed(1)}%` : '-'
                            },
                            {
                              title: 'P/B',
                              dataIndex: 'pb',
                              key: 'pb',
                              width: 60,
                              align: 'right',
                              render: (val) => val != null ? val.toFixed(2) : '-'
                            },
                          ]}
                        />
                        <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                          💡 These stocks passed the market cap filter but were excluded due to momentum or sector rules.
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* RESTORED: Events Table */}
                <div>
                  <Typography.Text strong>Events / Trades / Dividends</Typography.Text>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                    Includes all equity trades, dividends, and other portfolio events.
                  </div>
                  {eventsList && eventsList.length > 0 ? (
                    <Table
                      size="small"
                      rowKey={(_, idx) => idx}
                      pagination={{ pageSize: 20 }}
                      dataSource={eventsList}
                      columns={[
                        { title: 'Date', dataIndex: 'event_date', render: (val) => new Date(val).toISOString().split('T')[0] },
                        {
                          title: 'Type',
                          dataIndex: 'event_type',
                          render: (type, record) => {
                            // Highlight Dividends and Splits (case-insensitive)
                            const typeStr = String(type || '').trim().toLowerCase();
                            if (typeStr === 'dividend') {
                              return <Tag color="green">DIVIDEND</Tag>;
                            }
                            // DAS-1678: Highlight stock splits
                            if (typeStr === 'split') {
                              return <Tag color="purple">SPLIT</Tag>;
                            }
                            // DAS-1662: Make rebalance_buy trades clickable to show selection details
                            const reason = String(record.reason || '').trim().toLowerCase();
                            if (type === 'trade' && reason === 'rebalance_buy') {
                              return (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => openRebalancingDetails(record)}
                                  style={{ padding: 0, color: '#52c41a' }}
                                >
                                  {type.toUpperCase()}
                                </Button>
                              );
                            }
                            // DAS-1592: Make trade events clickable to show timeline
                            if (type === 'trade' && record.reason_meta?.clause_details?.length > 0) {
                              return (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => openTradeTimeline(record)}
                                  style={{ padding: 0 }}
                                >
                                  {type.toUpperCase()}
                                </Button>
                              );
                            }
                            return <Tag>{type.toUpperCase()}</Tag>;
                          },
                        },
                        { 
                          title: 'Ticker', 
                          dataIndex: 'ticker', 
                          // DAS-1778: Make ticker clickable to open SearchResultModal
                          render: (ticker) => (
                            <span 
                              style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchResultModal({ open: true, ticker });
                              }}
                            >
                              {renderTickerWithInfo(ticker)}
                            </span>
                          )
                        },
                        { title: 'Qty', dataIndex: 'qty', render: (val) => val != null ? Number(val).toFixed(2) : '-' },
                        { 
                          title: 'Amount / Price', 
                          dataIndex: 'price', 
                          render: (val, record) => {
                            const type = String(record.event_type || '').trim().toLowerCase();
                            if (type === 'dividend') {
                              // For dividends, show Total Amount (Qty * Price)
                              const total = (Number(record.qty || 0) * Number(val || 0));
                              return <span style={{ fontWeight: 'bold', color: '#52c41a' }}>+€{total.toFixed(2)}</span>;
                            }
                            // DAS-1678: For splits, show ratio from reason field
                            if (type === 'split') {
                              const reason = record.reason || '';
                              // Extract ratio from reason like "SPLIT ratio=2.0 (2:1)"
                              const match = reason.match(/ratio=([\d.]+)/);
                              const ratio = match ? match[1] : '-';
                              return <span style={{ color: '#722ed1' }}>{ratio}:1</span>;
                            }
                            return val != null ? Number(val).toFixed(2) : '-';
                          }
                        },
                        { title: 'Fees', dataIndex: 'fees', render: (val) => val != null ? Number(val).toFixed(2) : '-' },
                        {
                          title: 'Reason',
                          key: 'reason',
                          render: (_, record) => renderEventReason(record),
                        },
                      ]}
                    />
                  ) : (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#999', background: '#fafafa', border: '1px solid #eee', borderRadius: '4px' }}>
                      No events recorded for this run.
                    </div>
                  )}
                </div>
                {/* RESTORED: Splits Table */}
                {!!warningsData?.splits?.length && (
                  <div>
                    <Typography.Text strong>Splits within window</Typography.Text>
                    <Table
                      size="small"
                      rowKey={(_, idx) => idx}
                      pagination={{ pageSize: 10 }}
                      dataSource={warningsData.splits}
                      columns={[
                        { 
                          title: 'Ticker', 
                          dataIndex: 'ticker', 
                          // DAS-1778: Make ticker clickable to open SearchResultModal
                          render: (ticker) => (
                            <span 
                              style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchResultModal({ open: true, ticker });
                              }}
                            >
                              {renderTickerWithInfo(ticker)}
                            </span>
                          )
                        },
                        { title: 'Date', dataIndex: 'split_date' },
                        { title: 'Ratio', dataIndex: 'split_ratio' },
                        { title: 'From', dataIndex: 'split_from' },
                        { title: 'To', dataIndex: 'split_to' },
                        { title: 'Verified', dataIndex: 'verified' },
                        { title: 'Source', dataIndex: 'data_source' },
                      ]}
                    />
                  </div>
                )}
                
                {/* DAS-1712: Action buttons moved to end */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e8e8e8' }}>
                  <Space wrap>
                    <Button onClick={exportResultsCsv}>Export CSV</Button>
                    <Button onClick={copyResultsJson}>Copy JSON Results</Button>
                    <Button onClick={downloadResultsJson}>Download JSON Results</Button>
                    <Button onClick={downloadPricesAuditJson}>Download Price/FX Audit JSON</Button>
                    <Button onClick={copyRunConfigJson}>Copy JSON Run Configuration</Button>
                    <Button onClick={downloadRunConfigJson}>Download JSON Run Configuration</Button>
                    <Button type="primary" onClick={createRunJiraTicket}>Create Jira ticket</Button>
                    {onOpenConfiguration && (
                      <Button type="default" onClick={onOpenConfiguration}>
                        Edit Configuration
                      </Button>
                    )}
                    <Button
                      onClick={() => {
                        if (!universeJson) {
                          message.info('No universe debug available for this run yet. Execute a run first.');
                          return;
                        }
                        setUniverseOpen(true);
                      }}
                    >
                      View Universe
                    </Button>
                    <Button
                      danger={(() => {
                        // DAS-1690: Use same field as modal PASS/FAIL check (hierarchical_check.difference_3)
                        const THRESHOLD_EUR = 0.01; // €0.01 threshold for significant discrepancy
                        
                        // Primary check: hierarchical_check.difference_3 (same as modal PASS/FAIL)
                        if (eqCheck?.event_attribution?.hierarchical_check?.difference_3 != null) {
                          const diff = Math.abs(Number(eqCheck.event_attribution.hierarchical_check.difference_3));
                          return diff >= THRESHOLD_EUR;
                        }
                        // Fallback: no eqCheck data yet = don't show danger
                        return false;
                      })()}
                      onClick={async () => {
                        if (!runInfo?.run_id) return message.info('No run yet');
                        const data = await callAction('equity_check_no_events', { run_id: runInfo.run_id });
                        if (data?.success) {
                          setEqCheck(data.data || {});
                          setEqCheckOpen(true);
                        }
                      }}
                    >
                      Equity Curve Check{(() => {
                        // DAS-1690: Show warning emoji only if discrepancy >= €0.01 (same as modal)
                        const THRESHOLD_EUR = 0.01;
                        if (eqCheck?.event_attribution?.hierarchical_check?.difference_3 != null) {
                          const diff = Math.abs(Number(eqCheck.event_attribution.hierarchical_check.difference_3));
                          return diff >= THRESHOLD_EUR ? ' ⚠️' : '';
                        }
                        return '';
                      })()}
                    </Button>
                  </Space>
                </div>
              </Space>
            ),
          },
          {
            key: 'debug',
            label: 'Debug (Price/FX)',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Button onClick={loadPricesDebugData} loading={pricesDebugLoading}>
                  Load Debug Data
                </Button>
                {pricesDebugData && (
                  <>
                    <Space>
                      <Typography.Text strong>Select Date:</Typography.Text>
                      <Select
                        value={selectedDebugDate}
                        onChange={setSelectedDebugDate}
                        style={{ width: 200 }}
                        options={(pricesDebugData.dates || []).map(d => ({
                          value: d,
                          label: `${d} (Equity: €${(pricesDebugData.equity_eur && pricesDebugData.equity_eur[pricesDebugData.dates.indexOf(d)] != null ? pricesDebugData.equity_eur[pricesDebugData.dates.indexOf(d)] : 0).toFixed(2)})`
                        }))}
                      />
                    </Space>
                    {selectedDateDebugData && (
                      <>
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="Date">{selectedDateDebugData.date}</Descriptions.Item>
                          <Descriptions.Item label="Equity (EUR)">€{selectedDateDebugData.equity_eur?.toFixed(2)}</Descriptions.Item>
                          <Descriptions.Item label="Cash by Currency">
                            {Object.entries(selectedDateDebugData.cash_by_currency || {}).map(([curr, val]) => (
                              <div key={curr}>{curr}: {val?.toFixed(2)}</div>
                            ))}
                          </Descriptions.Item>
                        </Descriptions>
                        <Typography.Text strong style={{ marginTop: 16 }}>Positions on {selectedDebugDate}</Typography.Text>
                        <Table
                          dataSource={selectedDateDebugData.positions}
                          columns={[
                            { 
                              title: 'Ticker', 
                              dataIndex: 'ticker', 
                              key: 'ticker',
                              // DAS-1778: Make ticker clickable to open SearchResultModal
                              render: (ticker) => (
                                <span 
                                  style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSearchResultModal({ open: true, ticker });
                                  }}
                                >
                                  {ticker}
                                </span>
                              )
                            },
                            { title: 'Qty', dataIndex: 'qty', key: 'qty', render: (val) => val?.toFixed(2) },
                            { title: 'Curr', dataIndex: 'currency', key: 'currency' },
                            { title: 'Local Price', dataIndex: 'local_price', key: 'local_price', render: (val) => val?.toFixed(2) },
                            { title: 'FX Rate (EUR/Local)', dataIndex: 'fx_rate', key: 'fx_rate', render: (val) => val?.toFixed(4) },
                            { title: 'EUR Price', dataIndex: 'eur_price', key: 'eur_price', render: (val) => val?.toFixed(2) },
                            { title: 'Pos Value (EUR)', dataIndex: 'position_value_eur', key: 'position_value_eur', render: (val) => val?.toFixed(2) },
                            { title: 'Price Source', dataIndex: 'price_source_column', key: 'price_source_column' },
                          ]}
                          rowKey="ticker"
                          pagination={{ pageSize: 20 }}
                          scroll={{ x: 'max-content' }}
                        />
                      </>
                    )}
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />
      {tradeTimelineModal.open && (
        <TradeTimelineModal
          open={tradeTimelineModal.open}
          onClose={closeTradeTimeline}
          loading={tradeTimelineModal.loading}
          data={tradeTimelineModal.data}
          event={tradeTimelineModal.event}
          onCopyJson={copyTradeTimelineJson}
          onCreateTicket={createTradeTimelineJiraTicket}
        />
      )}
      {alertDetailModal.open && (
        <AlertDetailModal
          open={alertDetailModal.open}
          onClose={closeAlertDetails}
          loading={alertDetailModal.loading}
          alertId={alertDetailModal.alertId}
          summary={alertDetailModal.summary}
          fields={alertDetailModal.fields}
        />
      )}
      {clauseDetailModal.open && (
        <ClauseDetailModal
          open={clauseDetailModal.open}
          onClose={closeClauseDetails}
          meta={clauseDetailModal.meta}
          event={clauseDetailModal.event}
        />
      )}
      {rebalancingModal.open && (
        <RebalancingSelectionModal
          open={rebalancingModal.open}
          onClose={closeRebalancingDetails}
          runId={rebalancingModal.runId}
          ticker={rebalancingModal.ticker}
          rebalancingDate={rebalancingModal.rebalancingDate}
          callAction={callAction}
        />
      )}
      
      {/* DAS-1714: Ticker Events Detail Modal */}
      <Modal
        title={
          <div>
            <span>
              Events for <strong>{tickerDetailModal.ticker}</strong>
              {tickerDetailModal.sector && tickerDetailModal.sector !== 'Unknown' && (
                <Tooltip title={tickerDetailModal.industry || ''}>
                  <Tag color="blue" style={{ marginLeft: 8 }}>{tickerDetailModal.sector}</Tag>
                </Tooltip>
              )}
            </span>
            {tickerDetailModal.summary && (
              <div style={{ fontSize: 13, fontWeight: 'normal', color: '#666', marginTop: 4 }}>
                {tickerDetailModal.summary.buy_count || 0} buys, {tickerDetailModal.summary.sell_count || 0} sells
                {' '}({(tickerDetailModal.summary.buy_count || 0) + (tickerDetailModal.summary.sell_count || 0)} total trades)
              </div>
            )}
          </div>
        }
        open={tickerDetailModal.open}
        onCancel={() => setTickerDetailModal({ open: false, ticker: null, data: null, sector: null, industry: null })}
        footer={null}
        width={800}
      >
        {tickerDetailModal.data && tickerDetailModal.data.length > 0 ? (
          <>
            {tickerDetailModal.summary && (
              <div style={{ 
                marginBottom: 16, 
                padding: 12, 
                background: '#fafafa', 
                borderRadius: 6,
                display: 'flex',
                gap: 24,
                flexWrap: 'wrap'
              }}>
                <div>
                  <span style={{ color: '#666' }}>Start Qty:</span>{' '}
                  <strong>{tickerDetailModal.summary.start_qty?.toFixed(2) || '0.00'}</strong>
                </div>
                <div>
                  <span style={{ color: '#666' }}>End Qty:</span>{' '}
                  <strong>{tickerDetailModal.summary.end_qty?.toFixed(2) || '0.00'}</strong>
                </div>
                <div>
                  <span style={{ color: '#666' }}>Dividends:</span>{' '}
                  <strong style={{ color: '#52c41a' }}>
                    +€{(tickerDetailModal.summary.dividends_eur || 0).toFixed(2)}
                  </strong>
                </div>
                <div>
                  <span style={{ color: '#666' }}>Net Result:</span>{' '}
                  <strong style={{ 
                    color: (tickerDetailModal.summary.net_result_eur || 0) >= 0 ? '#52c41a' : '#cf1322' 
                  }}>
                    {(tickerDetailModal.summary.net_result_eur || 0) >= 0 ? '+' : ''}
                    €{(tickerDetailModal.summary.net_result_eur || 0).toFixed(2)}
                  </strong>
                </div>
              </div>
            )}
            <Table
              dataSource={tickerDetailModal.data}
              size="small"
              pagination={false}
              rowKey={(_, idx) => idx}
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'event_date',
                  key: 'date',
                  width: 100,
                  render: (val) => {
                    try {
                      return new Date(val).toISOString().split('T')[0];
                    } catch {
                      return val;
                    }
                  }
                },
                {
                  title: 'Type',
                  dataIndex: 'event_type',
                  key: 'type',
                  width: 110,
                  render: (type, record) => {
                    const qty = Number(record.qty) || 0;
                    const reason = record.reason || '';
                    const isNetted = reason.startsWith('NETTED:') || record.is_netted;
                    
                    if (type === 'dividend') return <Tag color="green">DIVIDEND</Tag>;
                    if (type === 'split') return <Tag color="purple">SPLIT</Tag>;
                    if (type === 'trade') {
                      // DAS-1719: Show netting info
                      if (isNetted) {
                        return (
                          <Tooltip title={reason}>
                            <Tag color="orange" style={{ cursor: 'help' }}>
                              {qty > 0 ? 'NET BUY' : 'NET SELL'}
                            </Tag>
                          </Tooltip>
                        );
                      }
                      return qty > 0 
                        ? <Tag color="blue">BUY</Tag> 
                        : <Tag color="red">SELL</Tag>;
                    }
                    return <Tag>{type?.toUpperCase()}</Tag>;
                  }
                },
                {
                  title: 'Qty',
                  dataIndex: 'qty',
                  key: 'qty',
                  width: 80,
                  align: 'right',
                  render: (val, record) => {
                    if (val == null || record.event_type === 'split') return '-';
                    const numVal = Number(val);
                    if (isNaN(numVal)) return '-';
                    return Math.abs(numVal).toFixed(2);
                  }
                },
                {
                  title: 'Price (€)',
                  dataIndex: 'price',
                  key: 'price',
                  width: 100,
                  align: 'right',
                  render: (val, record) => {
                    if (val == null || record.event_type === 'split') return '-';
                    const numVal = Number(val);
                    if (isNaN(numVal)) return '-';
                    if (record.event_type === 'dividend') {
                      return <span style={{ color: '#52c41a' }}>€{numVal.toFixed(4)}/sh</span>;
                    }
                    return `€${numVal.toFixed(2)}`;
                  }
                },
                {
                  title: 'Trade Value (€)',
                  dataIndex: 'trade_value',
                  key: 'trade_value',
                  width: 120,
                  align: 'right',
                  render: (val, record) => {
                    if (val == null || record.event_type === 'split') return '-';
                    const numVal = Number(val);
                    if (isNaN(numVal)) return '-';
                    const qty = Number(record.qty) || 0;
                    if (record.event_type === 'dividend') {
                      return <span style={{ color: '#52c41a' }}>+€{numVal.toFixed(2)}</span>;
                    }
                    return (
                      <span style={{ color: qty > 0 ? '#cf1322' : '#52c41a' }}>
                        {qty > 0 ? '-' : '+'}€{numVal.toFixed(2)}
                      </span>
                    );
                  }
                },
                {
                  title: 'Fees (€)',
                  dataIndex: 'fees',
                  key: 'fees',
                  width: 80,
                  align: 'right',
                  render: (val) => {
                    if (val == null) return '-';
                    const numVal = Number(val);
                    if (isNaN(numVal) || numVal === 0) return '-';
                    return <span style={{ color: '#cf1322' }}>€{numVal.toFixed(2)}</span>;
                  }
                }
              ]}
              summary={(pageData) => {
                const totalTradeValue = pageData.reduce((sum, r) => {
                  if (r.event_type === 'split' || r.trade_value == null) return sum;
                  const tradeVal = Number(r.trade_value) || 0;
                  const qty = Number(r.qty) || 0;
                  if (r.event_type === 'dividend') return sum + tradeVal;
                  return sum + (qty > 0 ? -tradeVal : tradeVal);
                }, 0);
                const totalFees = pageData.reduce((sum, r) => sum + (Number(r.fees) || 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                      <Table.Summary.Cell index={0} colSpan={4}>TOTAL</Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span style={{ color: totalTradeValue >= 0 ? '#52c41a' : '#cf1322' }}>
                          {totalTradeValue >= 0 ? '+' : ''}€{totalTradeValue.toFixed(2)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        {totalFees > 0 && (
                          <span style={{ color: '#cf1322' }}>€{totalFees.toFixed(2)}</span>
                        )}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </>
        ) : (
          <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>
            No events found for this ticker.
          </div>
        )}
      </Modal>

      {/* DAS-1778: SearchResultModal for full ticker details */}
      {searchResultModal.open && (
        <ThreeTablesModal
          tickerValue={searchResultModal.ticker}
          open={searchResultModal.open}
          onClose={() => setSearchResultModal({ open: false, ticker: null })}
          userId={getUserId()}
        />
      )}
    </>
  );
};

export default ResultsDrawer;
